
import type { Route } from 'vue-router/types/router'
import type { Store } from 'vuex/types'
import type { GuardResolveFunction } from '../../models'

import { computed, defineComponent, ref } from 'vue'
import ExplainFiltersAside from '../../components/explain/explain-filters-aside.component.vue'
import ExplainHeader from '../../components/explain/explain-header.component.vue'

import ExplainRealtimeQueries from '../../components/explain/explain-landing/explain-realtime-queries.component.vue'
import { EpbAnimatedBackground } from '../../components/shared/epb-animated-background'
import FieldValueSelect from '../../components/shared/field-value-select.component.vue'
import XExtraParams from '../../components/x/x-extra-params.component.vue'

import { useExplainBrowse } from '../../composables/explain/use-explain-browse'

import { useContextQuery } from '../../composables/use-context-query'
import { useEpb } from '../../composables/use-epb'
import { useExplainFilters } from '../../composables/use-explain-filters'
import { useExplainSectionsConfig } from '../../composables/use-explain-sections-config'
import { CONTAINER } from '../../config'
import { TYPES } from '../../config/inversify.types'
import { isExplainRoute } from '../../router/routes/explain.routes'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'

export default defineComponent({
  name: 'Explain',
  components: {
    EpbAnimatedBackground,
    ExplainHeader,
    ExplainFiltersAside,
    ExplainRealtimeQueries,
    FieldValueSelect,
    XExtraParams,
  },
  async beforeRouteLeave(to: Route, from: Route, next: GuardResolveFunction): Promise<void> {
    this.contextWatchDisabled = true

    next()
  },
  async beforeRouteEnter(to: Route, from: Route, next: GuardResolveFunction) {
    const store = CONTAINER.get<Store<any>>(TYPES.Store)
    if (!isExplainRoute(from)) {
      await store.dispatch(`${CONTEXT_CONSTS.moduleName}/${CONTEXT_CONSTS.actions.setQuery}`, '')
    }

    next()
  },
  setup: () => {
    const { $epb } = useEpb()
    const { query } = useContextQuery()
    const { currentSectionConfig, setCurrentSection } = useExplainSectionsConfig()
    const areAllFiltersMandatory = computed(() => currentSectionConfig.value.areAllFiltersMandatory)
    const contextWatchDisabled = ref(false)

    const { filters, showExplainFilters, showExplainFiltersAside, areFiltersValid } =
      useExplainFilters({
        areAllFiltersMandatory,
      })

    const explainBrowse = useExplainBrowse()

    const isSearchBoxDisabled = computed(() => !areFiltersValid.value)

    const areRealtimeQueriesVisible = computed((): boolean => {
      return areFiltersValid.value && !query.value && !explainBrowse.isBrowseContext.value
    })

    return {
      contextWatchDisabled,
      areRealtimeQueriesVisible,
      currentSectionConfig,
      ...explainBrowse,
      filters,
      filtersStartOpened: !areFiltersValid.value,
      inputQuery: '',
      isSearchBoxDisabled,
      messages: $epb.messages.explain,
      query,
      realtimeQueryCount: 15,
      showExplainFilters,
      showExplainFiltersAside,
      setCurrentSection,
    }
  },
  watch: {
    query: {
      handler(query: string): void {
        if (!this.contextWatchDisabled && query) {
          this.$router.replace({
            name: this.currentSectionConfig.routeName,
          })
        }
      },
    },
  },
  created(): void {},
  methods: {
    openFilters(): void {
      this.$refs.filtersAside.open()
    },
    handleInputQuery(inputQuery: string): void {
      this.inputQuery = inputQuery
    },
  },
})
